/**
 * LevelRewardsDrawer — anchor-themed level reward catalog.
 *
 * Right-edge slide drawer. Lists every level config the user can climb
 * with the prize, description, XP required, and FUEL bonus. The user's
 * current level row carries a red accent left rail and a "YOU ARE HERE"
 * marker; locked rows downshift in opacity; the Level 4 promo CTA
 * survives the rebuild between rows 3 and 4 (additive-only redesign).
 *
 * Data source — same as the legacy purple drawer:
 *   useGetLevelConfigs  → dynamic level configs from admin
 *   getGoVipLevelsText  → hardcoded fallback (8 levels)
 *   useGetUserLevel     → current-level progress + XP-to-next
 */

import {ReactElement, useEffect, useMemo} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router";
import {useStatsigClient} from "@statsig/react-bindings";
import classNames from "classnames";
import {Token} from "@devour/client";
import {IStore} from "@/redux/defaultStore";
import {useGetLevelConfigs} from "@/hooks/useGetLevelConfigs";
import {useGetUserProfile} from "@/hooks/useGetUserProfile";
import {useGetUserLevel} from "@/hooks/useGetUserLevel";
import {
    GoVipLevelsCardData,
    getGoVipLevelsText,
    mapLevelConfigToCardData,
} from "@/components/goVip/goVipText";
import {getGoVipLevelTokenReward, getGovipLevelImage} from "@/components/goVip/GoVipLevelCard";
import {
    decrementModalCount,
    incrementModalCount,
    toggleHideBottomNav,
} from "@/redux/meta/metaActions";
import "./LevelRewardsDrawer.scss";

interface Props {
    show: boolean;
    toggle: () => void;
}

const PROMO_AFTER_LEVEL = 3;

/* Quote + escape so a CDN url containing `)` or `"` can't break out of `url("…")`. */
const safeCssUrl = (url: string): string => `url("${url.replace(/"/g, "%22")}")`;

function LevelRewardsDrawer(props: Props): ReactElement {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {client: statsigClient} = useStatsigClient();
    const fuelStatus = !statsigClient.checkGate(import.meta.env.VITE_TOKEN_STATSIG_STATUS);
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken) as Token | undefined;

    const {data: levelConfigs, isPending: levelConfigsPending} = useGetLevelConfigs();
    const {data: userProfile} = useGetUserProfile(fullToken!);
    const {data: accountLevel, isLoading: accountLevelLoading} = useGetUserLevel(fullToken!, userProfile?.user?.id ?? "");
    /* Derived first: the stored `user.level` only moves on an XP event, so a player who just cleared a gate reads a level behind. */
    const currentLevel = accountLevel?.level ?? userProfile?.user?.level ?? 0;
    /* Pass or membership, either way they already have Level 4 — held back while loading so it cannot flash. */
    const showPromo = !accountLevelLoading && !accountLevel?.userOwnsIndustryCollectionNft;

    const xpThisLevel = accountLevel?.experienceGainedAtThisLevel ?? 0;
    const xpToNext = accountLevel?.experienceRequiredForNextLevel ?? 0;
    const xpTotalForLevel = xpThisLevel + xpToNext;
    const progressPct = xpTotalForLevel > 0
        ? Math.max(0, Math.min(100, Math.round(xpThisLevel / xpTotalForLevel * 100)))
        : 0;

    /*
     * Dynamic configs are the source of truth. The hardcoded table is a fallback for levels the
     * admin has not configured — but it must never render BEFORE the query settles, or the drawer
     * paints the old hardcoded catalog and then swaps to the live one (visible flash on open).
     */
    const levels: GoVipLevelsCardData[] = useMemo(() => {
        if (levelConfigsPending) return [];
        const dynamicByLevel = new Map((levelConfigs ?? []).map(c => [c.level, mapLevelConfigToCardData(c)]));
        const hardcodedByLevel = new Map(getGoVipLevelsText(fuelStatus).map(entry => [entry.level, {
            ...entry,
            fuelBonus: getGoVipLevelTokenReward(entry.level),
            imageUrl: getGovipLevelImage(entry.level),
        }]));
        const allLevels = [...new Set([...hardcodedByLevel.keys(), ...dynamicByLevel.keys()])].sort((a, b) => a - b);
        return allLevels.map(level => dynamicByLevel.get(level) ?? hardcodedByLevel.get(level)!);
    }, [levelConfigs, levelConfigsPending, fuelStatus]);

    useEffect(() => {
        if (props.show) {
            dispatch(incrementModalCount());
            dispatch(toggleHideBottomNav(true));
            return () => {
                dispatch(decrementModalCount());
                dispatch(toggleHideBottomNav(false));
            };
        }
        dispatch(toggleHideBottomNav(false));
    }, [props.show]);

    function handleClose(e: React.MouseEvent | React.TouchEvent): void {
        e.stopPropagation();
        props.toggle();
    }

    function rowState(level: number): "locked" | "current" | "unlocked" {
        if (currentLevel === 0) return "locked";
        if (level < currentLevel) return "unlocked";
        if (level === currentLevel) return "current";
        return "locked";
    }

    function renderLevelRow(data: GoVipLevelsCardData) {
        const state = rowState(data.level);
        return (
            <li
                key={`lvl-${data.level}`}
                className={classNames("lvl-row", `lvl-row--${state}`)}
                aria-current={state === "current" ? "true" : undefined}
            >
                <div
                    className="lvl-row-art"
                    style={data.imageUrl ? {backgroundImage: safeCssUrl(data.imageUrl)} : undefined}
                    aria-hidden="true"
                />
                <div className="lvl-row-body">
                    <div className="lvl-row-head">
                        <span className="lvl-row-lvl">LVL {String(data.level).padStart(2, "0")}</span>
                        {state === "current" && <span className="lvl-row-state lvl-row-state--current">You are here</span>}
                        {state === "unlocked" && <span className="lvl-row-state lvl-row-state--unlocked">Unlocked</span>}
                    </div>
                    <h3 className="lvl-row-prize">{data.prize}</h3>
                    <p className="lvl-row-desc">{data.description}</p>
                    {(data.xpRequired != null || data.fuelBonus != null && data.fuelBonus > 0) &&
                        <div className="lvl-row-meta">
                            {data.xpRequired != null &&
                                <span className="lvl-row-meta-cell">
                                    <span className="lvl-row-meta-value">{data.xpRequired.toLocaleString()}</span>
                                    <span className="lvl-row-meta-label">XP</span>
                                </span>
                            }
                            {data.fuelBonus != null && data.fuelBonus > 0 &&
                                <span className="lvl-row-meta-cell lvl-row-meta-cell--fuel">
                                    <img
                                        className="lvl-row-meta-icon"
                                        src="/images/Fuel Icon.png"
                                        alt=""
                                        aria-hidden="true"
                                    />
                                    <span className="lvl-row-meta-value">+${data.fuelBonus}</span>
                                    <span className="lvl-row-meta-label">Fuel</span>
                                </span>
                            }
                        </div>
                    }
                </div>
            </li>
        );
    }

    function renderPromoCard() {
        return (
            <li className="lvl-promo" key="lvl-promo">
                <div className="lvl-promo-body">
                    <div className="lvl-promo-eyebrow">FAST-TRACK</div>
                    <div className="lvl-promo-title">Ready to hit Level 4?</div>
                    <p className="lvl-promo-desc">
                        Grab the <span className="lvl-promo-emphasis">Industry Pass</span> and keep the grind going.
                    </p>
                </div>
                <button
                    type="button"
                    className="lvl-promo-cta"
                    onClick={() => {
                        props.toggle();
                        navigate("/the-industry-pass");
                    }}
                >
                    UNLOCK LEVEL 4
                </button>
            </li>
        );
    }

    function renderSkeletonRows(): ReactElement[] {
        return Array.from({length: 5}, (_, i) =>
            <li className="lvl-row lvl-row--skeleton" key={`lvl-skel-${i}`} aria-hidden="true">
                <div className="lvl-row-art" />
                <div className="lvl-row-body">
                    <div className="lvl-skel-line lvl-skel-line--sm" />
                    <div className="lvl-skel-line lvl-skel-line--lg" />
                    <div className="lvl-skel-line lvl-skel-line--md" />
                </div>
            </li>);
    }

    function renderRows(): ReactElement[] {
        const out: ReactElement[] = [];
        for (const data of levels) {
            out.push(renderLevelRow(data));
            if (data.level === PROMO_AFTER_LEVEL && showPromo) out.push(renderPromoCard());
        }
        return out;
    }

    return (
        <>
            <div
                className={classNames("level-rewards-drawer__overlay", {
                    "level-rewards-drawer__overlay--visible": props.show,
                })}
                onClick={handleClose}
                role="presentation"
            />
            <aside
                className={classNames("level-rewards-drawer", {"level-rewards-drawer--open": props.show})}
                role="dialog"
                aria-label="Level Rewards"
                aria-hidden={!props.show}
            >
                <header className="lvl-hd">
                    <div className="lvl-hd-row">
                        <div className="lvl-hd-eyebrow">REWARDS CATALOG</div>
                        <button
                            type="button"
                            className="lvl-hd-close"
                            onClick={handleClose}
                            aria-label="Close level rewards drawer"
                        >
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                    <h2 className="lvl-hd-title">LEVEL <span>REWARDS</span></h2>
                    <p className="lvl-hd-sub">Earn a prize every time you level up.</p>
                </header>

                <section className="lvl-status">
                    <div className="lvl-status-num">
                        <span className="lvl-status-num-label">CURRENT</span>
                        <span className="lvl-status-num-value">
                            {currentLevel > 0 ? String(currentLevel).padStart(2, "0") : "—"}
                        </span>
                    </div>
                    <div className="lvl-status-bar-wrap">
                        <div className="lvl-status-bar-row">
                            <span className="lvl-status-bar-label">
                                {currentLevel > 0 && xpTotalForLevel > 0
                                    ? <>{xpThisLevel.toLocaleString()} / {xpTotalForLevel.toLocaleString()} XP</>
                                    : "Log in to track progress"}
                            </span>
                            {currentLevel > 0 && xpTotalForLevel > 0 &&
                                <span className="lvl-status-bar-pct">{progressPct}%</span>
                            }
                        </div>
                        <div className="lvl-status-bar" role="progressbar" aria-valuenow={progressPct} aria-valuemin={0} aria-valuemax={100}>
                            <div className="lvl-status-bar-fill" style={{width: `${progressPct}%`}} />
                        </div>
                    </div>
                </section>

                <ul className="lvl-list" aria-busy={levelConfigsPending}>
                    {levelConfigsPending && renderSkeletonRows()}
                    {!levelConfigsPending && levels.length === 0 &&
                        <li className="lvl-empty" role="status">
                            <p className="lvl-empty-title">No level rewards configured</p>
                            <p className="lvl-empty-sub">Reward configs will appear here once published.</p>
                        </li>
                    }
                    {!levelConfigsPending && levels.length > 0 && renderRows()}
                </ul>
            </aside>
        </>
    );
}

export default LevelRewardsDrawer;
